'use client';

import Link from 'next/link';
import { useCallback, useState } from 'react';

import useBanners from '@flipster/features/src/banners/use-banners';
import { Carousel, rollingProps } from '@flipster/react/design-system-legacy/carousel';
import { Picture } from '@flipster/react/design-utils/picture';
import { css } from '@flipster/styled-system/css';
import { aspectRatio, flex, hstack, linkOverlay } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { EarnEvent } from '@flipster/utils/segment/events/earn';

export const Banner = () => {
  const { banners: allBanners } = useBanners({ image: ['EARN'] });
  const banners = allBanners?.image.EARN;

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // 배너가 swipe될 때 배너의 정보를 트래킹
  const trackEarnBannerView = useCallback(
    (activeIndex: number) => {
      if (!banners?.[activeIndex]) return;
      segment.track(EarnEvent.EarnBannerView, {
        image_name: banners[activeIndex].title,
        list_order: activeIndex,
      });
    },
    [banners],
  );

  if (!banners || banners.length === 0) return <div className={css(aspectRatio.raw({ ratio: 960 / 200 }))} />;
  return (
    <Carousel
      slidesPerView={1}
      {...rollingProps}
      navigator={
        banners.length > 1 ? (
          <div className={flex({ gridArea: 'arrow-bottom', justify: { base: 'center', tablet: 'flex-end' } })}>
            <div role='presentation' className={hstack({ display: { base: 'flex', tablet: 'none' }, gap: 4 })}>
              {banners.map((banner, i) => (
                <div
                  className={css({
                    w: 8,
                    h: 2,
                    bgColor: 'background.default.level-4',
                    '&[aria-current="true"]': { bgColor: 'inverse.white' },
                  })}
                  aria-current={currentIndex === i}
                  key={`indicator${banner.id}`}
                />
              ))}
            </div>
            <Carousel.Navigator css={{ display: { base: 'none', tablet: 'flex' } }} />
          </div>
        ) : undefined
      }
      onInit={() => trackEarnBannerView(0)}
      onChange={(i) => {
        setCurrentIndex(i);
        trackEarnBannerView(i);
      }}
      css={{ rowGap: 8 }}
      loop={false}
    >
      {banners.map((banner) => (
        <Carousel.Slide css={{ pos: 'relative' }} key={`slide${banner.id}`}>
          <Link
            href={banner.webLink ?? ''}
            onClick={() => {
              segment.track(EarnEvent.EarnBannerClick, { image_name: banner.title });
            }}
            className={linkOverlay()}
            tabIndex={-1}
            aria-hidden
          />
          <Picture
            src={{
              base: banner.mobileImage,
              tablet: banner.desktopImage,
            }}
            alt={`Go to ${banner.webLink} page`}
            width={960}
            height={200}
            css={{ w: '100%', h: 'auto', rounded: 16 }}
          />
        </Carousel.Slide>
      ))}
    </Carousel>
  );
};
